import { Box, Divider, Flex, SimpleGrid, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'

import { formatCurrency, formatCurrencyValue } from '../../helpers'
import { EventService } from '../../services/Event/EventService'
import { RevenueSummary } from '../../services/Event/types'
import ActionButton from '../ActionButton/ActionButton'
import { TrophyIcon } from '../icons'
import { LoadingBlock } from '../Loading'
import { SectionCard } from '../SectionCard'
import ParkingEditModal from './ParkingEditModal'
import { RevenueCard } from './RevenueCard'

Chart.register(...registerables)

// Cores das fatias de "Origem da receita de ingressos", na ordem que a API devolve.
const commissionHexByType: Record<string, string> = {
  none: '#A0AEC0',
  teacher: '#805AD5',
  partner: '#DD6B20',
  commercial: '#319795'
}

const doughnutOpts = {
  cutout: '70%',
  plugins: { legend: { display: false }, tooltip: { enabled: true } },
  responsive: true,
  maintainAspectRatio: true
}

type Props = {
  eventSlug?: string
  eventId?: number
  contestId?: number
  hasContests?: boolean
  costsTotal?: number | null
}

const eventService = new EventService()

function LegendItem({
  color,
  label,
  value,
  action,
  hint
}: {
  color: string
  label: string
  value: number | null
  action?: ReactNode
  /** Linha secundária sob o rótulo, para dizer de onde o número veio. */
  hint?: ReactNode
}) {
  return (
    <Flex align={hint ? 'flex-start' : 'center'} gap={2}>
      <Box w="10px" h="10px" borderRadius="sm" bg={color} flexShrink={0} mt={hint ? '5px' : 0} />
      <Box flex="1">
        <Text variant="caption">{label}</Text>
        {hint}
      </Box>
      {action}
      <Text fontSize="sm" fontWeight="600" color="gray.700">
        {value != null ? formatCurrency(value) : '—'}
      </Text>
    </Flex>
  )
}

export function RevenueSummaryCards({ eventSlug, eventId, contestId, hasContests, costsTotal }: Props) {
  const [revenue, setRevenue] = useState<RevenueSummary | null>(null)
  const [parkingRevenue, setParkingRevenue] = useState<number | null>(null)
  // Um evento ligado a um ticket do Abra Park tem a receita de estacionamento
  // puxada de lá (ABRA-252); só o valor manual continua editável aqui.
  const isParkingFromAbraPark = revenue?.parking_revenue_source === 'abra_park'
  const { isOpen: isParkingOpen, onOpen: onParkingOpen, onClose: onParkingClose } = useDisclosure()

  useEffect(() => {
    if (!eventSlug && !contestId) return
    eventService
      .getRevenueSummary({ event_slug: eventSlug, contest_id: contestId })
      .then((data) => {
        setRevenue(data)
        setParkingRevenue(Number(data.parking_revenue) || 0)
      })
      .catch(() => undefined)
  }, [eventSlug, contestId])

  const hasEvent = revenue?.event != null

  const computedTotal = useMemo(
    () =>
      revenue != null
        ? (revenue.event_revenue ?? 0) +
          (revenue.contests_revenue ?? 0) +
          (revenue.auction_revenue ?? 0) +
          (revenue.system_fee_revenue ?? 0) +
          (revenue.establishments_revenue ?? 0) +
          (parkingRevenue ?? 0)
        : null,
    [revenue, parkingRevenue]
  )

  if (contestId) {
    return (
      <SimpleGrid columns={{ base: 1, md: hasEvent ? 3 : 1 }} spacing={4} mb={6}>
        <RevenueCard
          label="Arrecadado no concurso"
          value={revenue?.contests_revenue ?? null}
          icon={<TrophyIcon />}
          color="green.500"
        />
        {hasEvent && (
          <RevenueCard
            label="Arrecadado no evento"
            value={revenue?.event_revenue ?? null}
            icon="lni lni-calendar"
            color="teal.500"
          />
        )}
        {hasEvent && (
          <RevenueCard
            label="Total geral"
            value={revenue?.total_revenue ?? null}
            icon="lni lni-wallet"
            color="pink.500"
            highlight
          />
        )}
      </SimpleGrid>
    )
  }

  const result = computedTotal != null ? computedTotal - (costsTotal ?? 0) : null
  const segments: { label: string; chakraColor: string; hex: string; value: number }[] = [
    ...(hasContests
      ? [{ label: 'Concurso', chakraColor: 'green.500', hex: '#38A169', value: revenue?.contests_revenue ?? 0 }]
      : []),
    { label: 'Evento', chakraColor: 'teal.500', hex: '#319795', value: revenue?.event_revenue ?? 0 },
    ...(hasContests
      ? [{ label: 'Leilão', chakraColor: 'orange.500', hex: '#DD6B20', value: revenue?.auction_revenue ?? 0 }]
      : []),
    // ABRA-338 — taxa sistêmica cobrada por cima do lance de cada obra arrematada.
    ...((revenue?.system_fee_revenue ?? 0) > 0
      ? [
          {
            label: 'Taxa sistêmica',
            chakraColor: 'cyan.500',
            hex: '#00B5D8',
            value: revenue?.system_fee_revenue ?? 0
          }
        ]
      : []),
    { label: 'Estacionamento', chakraColor: 'blue.500', hex: '#3182CE', value: parkingRevenue ?? 0 },
    // ABRA-317 — só a taxa da ABRA nas vendas dos estabelecimentos. "Parceiros" já é a fatia dos
    // cupons no gráfico de origem, ao lado; aqui o nome é outro para não confundir os dois.
    ...((revenue?.establishments_revenue ?? 0) > 0
      ? [
          {
            label: 'Estabelecimentos',
            chakraColor: 'purple.500',
            hex: '#805AD5',
            value: revenue?.establishments_revenue ?? 0
          }
        ]
      : [])
  ]

  const commissionSegments = (revenue?.event_revenue_by_commission ?? []).map((slice) => ({
    label: slice.label,
    hex: commissionHexByType[slice.type] ?? '#CBD5E0',
    value: slice.value
  }))
  // Sem receita de ingressos não há nada a repartir — o gráfico some em vez de mostrar zeros.
  const hasCommissionData = commissionSegments.some((s) => s.value > 0)

  const hasRevenueData = segments.some((s) => s.value > 0)
  const chartData = {
    labels: hasRevenueData ? segments.map((s) => s.label) : ['Sem receita'],
    datasets: [
      {
        data: hasRevenueData ? segments.map((s) => s.value) : [1],
        backgroundColor: hasRevenueData ? segments.map((s) => s.hex) : ['#EDF2F7'],
        borderWidth: 0,
        borderRadius: hasRevenueData ? 4 : 0
      }
    ]
  }

  const totalCenterStr =
    computedTotal == null
      ? null
      : computedTotal >= 1000
        ? `${(computedTotal / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1 })}k`
        : formatCurrencyValue(computedTotal)

  return (
    <>
      {/* Cards: Receita Total (always) + Total de Custos + Resultado (when costs available) */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={6}>
        <RevenueCard label="Receita Total" value={computedTotal} icon="lni lni-wallet" color="green.500" highlight />
        <RevenueCard label="Total de Custos" value={costsTotal ?? null} icon="lni lni-coin" color="red.500" />
        <RevenueCard
          label="Resultado"
          value={result}
          icon="lni lni-stats-up"
          color={result != null && result < 0 ? 'red.500' : 'green.500'}
          highlight
        />
      </SimpleGrid>

      {/* Dois cards lado a lado, como "Visão Geral de Ingressos" e "Informações do Evento".
          Sem receita de ingressos a repartir, o de origem some e o de receita ocupa a linha. */}
      <SimpleGrid columns={{ base: 1, lg: hasCommissionData ? 2 : 1 }} spacing={6} mb={4}>
        <SectionCard title="Receita">
          {revenue == null ? (
            <LoadingBlock h="200px" />
          ) : (
            <Flex align="center" justify="center" gap={{ base: 6, md: 8 }} direction={{ base: 'column', md: 'row' }}>
              <Box w="180px" h="180px" position="relative" flexShrink={0}>
                <Doughnut data={chartData} options={doughnutOpts} />
                {totalCenterStr && (
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    direction="column"
                    align="center"
                    pointerEvents="none"
                  >
                    <Text fontSize="xl" fontWeight="800" lineHeight="1" color="gray.800">
                      {totalCenterStr}
                    </Text>
                    <Text variant="caption" mt={0.5}>
                      total
                    </Text>
                  </Flex>
                )}
              </Box>

              <VStack align="stretch" spacing={3} flex="1" maxW="340px">
                {segments.map((seg) => (
                  <LegendItem
                    key={seg.label}
                    color={seg.chakraColor}
                    label={seg.label}
                    value={seg.value}
                    action={
                      // O lápis some quando o valor vem do Abra Park: lá é a fonte
                      // da verdade, e um número digitado aqui seria sobrescrito na
                      // próxima sincronização (a API recusa a edição nesse caso).
                      seg.label === 'Estacionamento' && eventId && !isParkingFromAbraPark ? (
                        <ActionButton
                          action="edit"
                          label="Editar valor"
                          size="xs"
                          variant="ghost"
                          color="gray.400"
                          _hover={{ color: 'blue.500' }}
                          onClick={onParkingOpen}
                        />
                      ) : undefined
                    }
                    hint={
                      seg.label === 'Estabelecimentos' ? (
                        <Text variant="caption">Só a taxa da ABRA sobre as vendas</Text>
                      ) : seg.label === 'Estacionamento' && isParkingFromAbraPark ? (
                        <Text fontSize="xs" color={revenue?.parking_revenue_stale ? 'orange.500' : 'gray.400'}>
                          {revenue?.parking_revenue_stale
                            ? 'ABRA Park indisponível — último valor sincronizado'
                            : `ABRA Park · ${revenue?.parking_ticket_type_name ?? 'ticket associado'}`}
                        </Text>
                      ) : undefined
                    }
                  />
                ))}
                <Divider />
                <Flex justify="space-between" align="center">
                  <Text variant="caption">Total</Text>
                  <Text fontSize="sm" fontWeight="700" color="gray.800">
                    {computedTotal != null ? formatCurrency(computedTotal) : '—'}
                  </Text>
                </Flex>
              </VStack>
            </Flex>
          )}
        </SectionCard>

        {/* Só a receita de ingressos passa por cupom, então só ela se divide por
            comissionamento (ABRA-258). */}
        {hasCommissionData && (
          <SectionCard title="Origem da receita de ingressos">
            <Flex align="center" justify="center" gap={{ base: 6, md: 8 }} direction={{ base: 'column', md: 'row' }}>
              <Box w="180px" h="180px" flexShrink={0}>
                <Doughnut
                  data={{
                    labels: commissionSegments.map((s) => s.label),
                    datasets: [
                      {
                        data: commissionSegments.map((s) => s.value),
                        backgroundColor: commissionSegments.map((s) => s.hex),
                        borderWidth: 0,
                        borderRadius: 4
                      }
                    ]
                  }}
                  options={doughnutOpts}
                />
              </Box>

              <VStack align="stretch" spacing={3} flex="1" maxW="340px">
                {commissionSegments.map((seg) => (
                  <LegendItem key={seg.label} color={seg.hex} label={seg.label} value={seg.value} />
                ))}
              </VStack>
            </Flex>
          </SectionCard>
        )}
      </SimpleGrid>

      <ParkingEditModal
        isOpen={isParkingOpen}
        onClose={onParkingClose}
        value={parkingRevenue}
        eventId={eventId}
        onSaved={(newVal) => setParkingRevenue(newVal)}
      />
    </>
  )
}
